// ============================================================
// LinkedClientsCard.js — linked clients (2026-10-28)
// decisions.md "Linked clients: one rota across two branches"
//
// Links this client to others under the same tenant (for example two
// branches whose office staff and carers work for both). Each client stays
// separate; the link lets the rota show and check them together, and billing
// charge a person in more than one of them once, on the main client.
// Removing the link splits them again with nothing to move.
// Confirmation is in the page (never window.confirm, Gotcha #147).
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '../../components/Toaster';
import { getClientLinkedClients, putClientLinkedClients } from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

export default function LinkedClientsCard({ clientId, clientName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [mainId, setMainId] = useState(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const apply = useCallback((res) => {
    setData(res);
    const members = res.group ? res.group.members.map((m) => m.clientId).filter((id) => id !== clientId) : [];
    setSelected(new Set(members));
    setMainId(res.group ? res.group.mainClientId : clientId);
  }, [clientId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      apply(await getClientLinkedClients(clientId));
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, apply]);

  useEffect(() => { load(); }, [load]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (mainId === id) setMainId(clientId);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function save(memberClientIds, mainClientId, successMessage) {
    setBusy(true);
    setError(null);
    try {
      const res = await putClientLinkedClients(clientId, { memberClientIds, mainClientId });
      toast.success(res.wasIdempotent ? 'No change' : successMessage);
      setConfirmingRemove(false);
      await load();
    } catch (err) {
      setError(translateError(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (!data) return error ? <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage> : null;

  const group = data.group;
  const candidates = data.candidates || [];
  const memberIds = [clientId, ...Array.from(selected)];
  const nameOf = (id) => (id === clientId ? clientName : (candidates.find((c) => c.clientId === id) || {}).clientName || `Client ${id}`);
  const canSave = selected.size >= 1 && memberIds.includes(mainId) && !busy;

  return (
    <div className="pc-linked">
      <p className="pc-explain">
        Link this client to others under the same tenant when their staff work for more than one of them, for
        example two branches. Each client stays separate: service users, care records, HR records and reports
        do not move. Linked clients get one rota view, and the rota checks a carer&apos;s clashes, leave and hours
        across all of them. A person with a login in more than one linked client is charged one seat, on the
        main client. Removing the link splits them again.
      </p>

      {error && <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>}

      {data.tenantId == null && (
        <StatusMessage as="p" variant="empty" className="pc-state">This client has no tenant, so it cannot be linked.</StatusMessage>
      )}

      {data.tenantId != null && candidates.length === 0 && (
        <StatusMessage as="p" variant="empty" className="pc-state">There are no other clients under this tenant to link to.</StatusMessage>
      )}

      {group && (
        <p className="pc-state">
          Linked with {group.members.filter((m) => m.clientId !== clientId).map((m) => m.clientName).join(', ')}.
          Main client: {nameOf(group.mainClientId)}.
        </p>
      )}

      {candidates.length > 0 && (
        <fieldset className="pc-linked-members" disabled={busy}>
          <legend className="pc-linked-legend">Link with</legend>
          {candidates.map((c) => (
            <label key={c.clientId} className="pc-checkbox">
              <input
                type="checkbox"
                checked={selected.has(c.clientId)}
                onChange={() => toggle(c.clientId)}
                disabled={c.inOtherGroup}
              />{' '}
              {c.clientName}
              {c.inOtherGroup && <span className="pc-linked-hint"> (already linked to other clients)</span>}
            </label>
          ))}
        </fieldset>
      )}

      {selected.size >= 1 && (
        <fieldset className="pc-linked-main" disabled={busy}>
          <legend className="pc-linked-legend">Main client (where a shared person&apos;s seat is charged)</legend>
          {memberIds.map((id) => (
            <label key={id} className="pc-checkbox">
              <input type="radio" name={`linked-main-${clientId}`} checked={mainId === id} onChange={() => setMainId(id)} />{' '}
              {nameOf(id)}
            </label>
          ))}
        </fieldset>
      )}

      <div className="pc-actions">
        <button
          type="button"
          className="pc-btn-primary"
          onClick={() => save(memberIds, mainId, 'Linked clients saved')}
          disabled={!canSave}
        >
          {busy ? 'Saving…' : 'Save link'}
        </button>
        {group && !confirmingRemove && (
          <button type="button" className="pc-btn-secondary" onClick={() => setConfirmingRemove(true)} disabled={busy}>
            Remove this client from the link
          </button>
        )}
      </div>

      {group && confirmingRemove && (
        <div className="pc-linked-confirm">
          <p className="pc-state">
            {group.members.length <= 2
              ? 'This removes the link. Both clients go back to their own rota; nothing is moved or deleted.'
              : `This takes ${clientName} out of the link. The other clients stay linked; nothing is moved or deleted.`}
          </p>
          <div className="pc-actions">
            <button type="button" className="pc-btn-primary" onClick={() => save([], null, 'Link removed')} disabled={busy}>
              {busy ? 'Removing…' : 'Remove from link'}
            </button>
            <button type="button" className="pc-btn-secondary" onClick={() => setConfirmingRemove(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
